/**
 * Recursively sort object based on its keys
 */
export function sort(
  obj: Record<string, any>,
  sortFn?: (a: string, b: string) => number
): Record<string, any> {
  return Object.keys(obj)
    .sort(sortFn)
    .reduce((sortedObj: Record<string, any>, key) => {
      sortedObj[key] =
        typeof obj[key] === 'object' ? sort(obj[key], sortFn) : obj[key];
      return sortedObj;
    }, {});
}

/**
 * https://stackoverflow.com/a/5878101/3220101
 */
export function isPlain(obj: any): obj is Record<string, any> {
  if (typeof obj === 'object' && obj !== null) {
    if (typeof Object.getPrototypeOf === 'function') {
      const proto = Object.getPrototypeOf(obj);
      return proto === Object.prototype || proto === null;
    }
    return Object.prototype.toString.call(obj) === '[object Object]';
  }
  return false;
}

/**
 * Naive, but cheap, deep clone. Will SO if has circular reference
 */
export function deepClone<T extends Record<string, any> | any[]>(src: T): T {
  return Object.entries(src).reduce(
    (dest: any, [key, value]) => {
      dest[key] =
        Array.isArray(value) || isPlain(value) ? deepClone(value) : value;
      return dest;
    },
    Array.isArray(src) ? [] : {}
  );
}

export function get(
  obj: Record<string, any>,
  path: string,
  valueIfMissing: any = undefined
): any {
  const keys = path.split('.');
  let key: string | undefined;
  let value: any = obj;

  while (keys.length > 0) {
    key = keys.shift();
    if (value && typeof value === 'object' && key && key in value) {
      value = value[key];
    } else {
      return valueIfMissing;
    }
  }

  return value;
}

export function exists(obj: Record<string, any>, path: string): boolean {
  const testObj = {};
  return get(obj, path, testObj) !== testObj;
}

export function set(
  obj: Record<string, any>,
  path: string,
  value: any
): Record<string, any> {
  const clone = deepClone(obj);
  path.split('.').reduce((slice: any, key, idx, keys) => {
    const nextKey = keys[idx + 1];
    if (!nextKey) {
      // last key
      slice[key] = value;
    } else if (!slice[key]) {
      slice[key] = Number.isInteger(parseFloat(nextKey)) ? [] : {};
    }

    return slice[key];
  }, clone);

  return clone;
}

export function remove(
  obj: Record<string, any>,
  path: string
): Record<string, any> {
  const clone = deepClone(obj);
  path.split('.').reduce((slice: any, key, idx, keys) => {
    if (idx === keys.length - 1) {
      // last key
      if (Array.isArray(slice)) {
        slice.splice(Number(key), 1);
      } else {
        delete slice[key];
      }
    }
    return slice[key];
  }, clone);

  return clone;
}

export function isEmpty(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0;
}

/**
 * Given multiple object paths, returns a reference to the first value found,
 * otherwise returns valueIfMissing
 */
export function coalesceGet(
  obj: Record<string, any>,
  paths: string[],
  valueIfMissing: any = undefined
): any {
  if (!paths[0]) {
    return valueIfMissing;
  }

  const value = get(obj, paths[0]);

  if (value !== undefined) {
    return value;
  }

  return coalesceGet(obj, paths.slice(1), valueIfMissing);
}

/**
 * This function will take in an object and will remove any properties that are undefined or null
 * It supports nested objects as well
 * @param obj the object that you would like to strip all null / undefined values out of
 * @returns a copy of the object without any null or undefined values present
 */
export function removeEmptyValues(
  obj: Record<string, any> | null | undefined
): Record<string, any> | null | undefined {
  if (obj == null) {
    return obj;
  }

  return Object.entries(obj)
    .filter(([_k, v]) => v != null)
    .reduce(
      (acc: Record<string, any>, [k, v]) => ({
        ...acc,
        [k]: v === Object(v) ? removeEmptyValues(v) : v
      }),
      {}
    );
}
